import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, ShieldCheck, RefreshCw, Users, Mail } from "lucide-react";

// Fliboe Pulse — About / Editorial policy page.
// Exists mainly to satisfy Google's E-E-A-T signals (Experience, Expertise,
// Authoritativeness, Trust) — every ranking Pulse article back-links to this
// page so raters and the ranking algorithm see a real editorial layer.

export default function PulseAbout() {
  useEffect(() => {
    document.title = "About Fliboe Pulse — Editorial Policy & Team";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", "Fliboe Pulse editorial policy — how our articles are written, reviewed, and updated. Meet the team behind Fliboe.");
    // AboutPage schema
    const canonical = `${window.location.origin}/pulse/about`;
    let can = document.querySelector('link[rel="canonical"]');
    if (!can) { can = document.createElement("link"); can.setAttribute("rel", "canonical"); document.head.appendChild(can); }
    can.setAttribute("href", canonical);
    const ld = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Fliboe Pulse",
      "url": canonical,
      "publisher": { "@type": "Organization", "name": "Fliboe", "url": window.location.origin },
    };
    let s = document.getElementById("about-jsonld");
    if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.id = "about-jsonld"; document.head.appendChild(s); }
    s.textContent = JSON.stringify(ld);
  }, []);

  return (
    <main className="min-h-screen bg-[#050505] text-white pb-24" data-testid="pulse-about">
      <div className="max-w-3xl mx-auto px-5 pt-10">
        <Link to="/pulse" className="text-white/50 text-sm inline-flex items-center gap-1 hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4" /> Fliboe Pulse
        </Link>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/30 text-fuchsia-200 text-xs font-semibold tracking-wide uppercase mb-4">
          <Sparkles className="w-3.5 h-3.5" /> About Fliboe Pulse
        </div>

        <h1 className="fl-display text-4xl sm:text-5xl font-black tracking-tight leading-[1.02]">Real writing.<br /><span className="text-emerald-400">AI-assisted, human-reviewed.</span></h1>

        <p className="mt-6 text-lg text-slate-300 leading-relaxed">
          Fliboe Pulse is the editorial arm of the Fliboe brand. We publish long-form articles about internet radio, indie audiobooks, independent film, home repair, cosmic astrology, and everything in between — everything that happens inside the Fliboe ecosystem.
        </p>

        <section className="mt-10 space-y-3">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Editorial policy</div>
          <ul className="space-y-2 text-slate-300">
            <li className="flex gap-2"><span className="text-emerald-400 mt-1">•</span> Every article is drafted by Anthropic Claude Sonnet 4.5 from a topic brief the creator or editor supplies.</li>
            <li className="flex gap-2"><span className="text-emerald-400 mt-1">•</span> Every published article is <strong className="text-white">reviewed by a human editor</strong> before it goes live on a Pulse site.</li>
            <li className="flex gap-2"><span className="text-emerald-400 mt-1">•</span> Auto-generated posts from sub-apps (Flip books, Showo films, FM stations) are disabled by default and only turn on when the creator explicitly opts in.</li>
            <li className="flex gap-2"><span className="text-emerald-400 mt-1">•</span> Articles are refreshed every 90 days to keep facts, links, and cultural context current.</li>
            <li className="flex gap-2"><span className="text-emerald-400 mt-1">•</span> Corrections are welcome — email us at <a href="mailto:editor@fliboe.com" className="text-emerald-300 hover:underline">editor@fliboe.com</a> and we'll update within 48 hours.</li>
          </ul>
        </section>

        <section className="mt-10 space-y-3">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Freshness</div>
          <p className="text-slate-300">Every Pulse article shows a <em>Published</em> date and, once refreshed, an <em>Updated</em> date. If the article carries an Updated stamp, the facts, streaming links, and cultural references have been re-verified by our editor on that date.</p>
        </section>

        <section className="mt-10 space-y-3">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Who's behind it</div>
          <p className="text-slate-300">Fliboe is founded and edited by <strong className="text-white">Anup Poudel</strong>, a founder-developer with 15+ years building radio, audiobook, and film platforms. Anup personally reviews Fliboe Pulse's editorial workflow and reads every reader-flagged correction.</p>
        </section>

        <section className="mt-10 space-y-3">
          <div className="fl-mono text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Contact</div>
          <p className="text-slate-300">Tips, corrections, or pitches — write to <a href="mailto:editor@fliboe.com" className="text-emerald-300 hover:underline">editor@fliboe.com</a>. For general questions, use <Link to="/contact" className="text-emerald-300 hover:underline">/contact</Link>.</p>
        </section>

        <div className="mt-14 text-white/30 text-[10px] fl-mono uppercase tracking-widest">
          Last reviewed: {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long" })}
        </div>
      </div>
    </main>
  );
}
